import { useState, useEffect } from 'react';

export default function PhotoUpload({ profilePic, setProfilePic, galleryPhotos, setGalleryPhotos, onSave, saving }) {
  const [uploadMsg, setUploadMsg] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [visibleGalleryOverlay, setVisibleGalleryOverlay] = useState(null); // Track which gallery overlay is visible on mobile

  // Handle keyboard navigation in preview modal
  useEffect(() => {
    if (!previewModal) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setPreviewModal(false);
      }
      // Left arrow: previous gallery photo
      if (e.key === 'ArrowLeft' && selectedPhoto?.type === 'gallery') {
        const newIndex = selectedPhoto.index > 0 ? selectedPhoto.index - 1 : galleryPhotos.length - 1;
        if (galleryPhotos[newIndex]) {
          setSelectedPhoto({ type: 'gallery', url: galleryPhotos[newIndex], index: newIndex });
        }
      }
      // Right arrow: next gallery photo
      if (e.key === 'ArrowRight' && selectedPhoto?.type === 'gallery') {
        const newIndex = selectedPhoto.index < galleryPhotos.length - 1 ? selectedPhoto.index + 1 : 0;
        if (galleryPhotos[newIndex]) {
          setSelectedPhoto({ type: 'gallery', url: galleryPhotos[newIndex], index: newIndex });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewModal, selectedPhoto, galleryPhotos]);

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setUploadMsg({ ok: false, text: 'Profile picture must be less than 5MB' });
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfilePic(event.target.result);
        setUploadMsg({ ok: true, text: 'Profile picture updated!' });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryPhotoChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setUploadMsg({ ok: false, text: `Photo ${index + 1} must be less than 5MB` });
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const newPhotos = [...galleryPhotos];
        newPhotos[index] = event.target.result;
        setGalleryPhotos(newPhotos);
        setUploadMsg({ ok: true, text: `Photo ${index + 1} updated!` });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeGalleryPhoto = (index) => {
    const newPhotos = [...galleryPhotos];
    newPhotos[index] = null;
    setGalleryPhotos(newPhotos);
    setUploadMsg({ ok: true, text: `Photo ${index + 1} removed!` });
  };

  return (
    <div style={{ marginBottom: 'clamp(1rem, 3vw, 2rem)', scrollMarginTop: 'clamp(60px, 10vh, 120px)' }}>
      {uploadMsg && (
        <div className={`al ${uploadMsg.ok ? 'al-ok' : 'al-er'}`} style={{ marginBottom: 'clamp(0.8rem, 2vw, 1.2rem)' }}>
          {uploadMsg.text}
        </div>
      )}

      {/* Profile Picture Section */}
      <div className="card" style={{ marginBottom: 'clamp(0.8rem, 2vw, 1.2rem)' }}>
        <div style={{
          fontFamily: 'var(--serif)',
          fontSize: 'clamp(1rem, 2.5vw, 1.15rem)',
          fontWeight: 600,
          marginBottom: 'clamp(0.7rem, 2vw, 1.1rem)',
          paddingBottom: 'clamp(0.4rem, 1vw, 0.65rem)',
          borderBottom: '1px solid var(--cream-d)'
        }}>
          📸 Profile Picture
        </div>
        <div style={{
          display: 'flex',
          gap: 'clamp(1rem, 3vw, 2rem)',
          alignItems: 'flex-start',
          flexWrap: 'wrap'
        }}>
          {/* Preview Container */}
          <div
            style={{
              position: 'relative',
              width: 'clamp(120px, 30vw, 200px)',
              height: 'clamp(120px, 30vw, 200px)',
              borderRadius: 'var(--r)',
              border: profilePic ? '2px solid var(--rose)' : '2px dashed var(--cream-d)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'clamp(2rem, 6vw, 3rem)',
              color: 'var(--cream-d)',
              background: profilePic ? '#fff' : 'var(--cream-l)',
              overflow: 'hidden',
              transition: 'var(--ease)',
              boxShadow: profilePic ? '0 4px 12px rgba(200, 70, 50, 0.15)' : 'none',
              cursor: profilePic ? 'default' : 'pointer',
              flexShrink: 0
            }}
            title={profilePic ? 'Tap to preview' : 'No photo uploaded'}
          >
              {/* Image */}
              {profilePic && (
                <img
                  src={profilePic}
                  alt="Profile"
                  onError={(e) => {
                    console.error('Profile image failed to load:', profilePic);
                    console.error('Error:', e);
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    padding: 'clamp(0.2rem, 1vw, 0.4rem)'
                  }}
                />
              )}
            
            {!profilePic && '📷'}

            {/* Hidden file input for empty state */}
            {!profilePic && (
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePicChange}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer'
                }}
              />
            )}
            
            {/* Overlay with icons for when photo exists - always visible on mobile */}
            {profilePic && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'clamp(0.4rem, 2vw, 0.8rem)',
                  transition: 'var(--ease)',
                  zIndex: 10,
                  pointerEvents: 'auto',
                  flexWrap: 'wrap'
                }}
                onMouseEnter={(e) => {
                  // Desktop: show overlay on hover
                  if (window.matchMedia('(min-width: 769px)').matches) {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                  }
                }}
                onMouseLeave={(e) => {
                  // Desktop: hide overlay on mouse leave
                  if (window.matchMedia('(min-width: 769px)').matches) {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
                  }
                }}
              >
                {/* Peek Icon */}
                <button
                  type="button"
                  onClick={(e) => { 
                    e.preventDefault();
                    e.stopPropagation(); 
                    setSelectedPhoto({ type: 'profile', url: profilePic }); 
                    setPreviewModal(true); 
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: 'none',
                    borderRadius: '50%',
                    width: 'clamp(40px, 10vw, 50px)',
                    height: 'clamp(40px, 10vw, 50px)',
                    color: 'var(--rose)',
                    cursor: 'pointer',
                    fontSize: 'clamp(1rem, 3vw, 1.3rem)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    transition: 'var(--ease)',
                    padding: 0,
                    zIndex: 11,
                    pointerEvents: 'auto',
                    touchAction: 'manipulation'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Preview photo"
                >
                  👁️
                </button>

                {/* Edit Icon */}
                <button
                  type="button"
                  onClick={(e) => { 
                    e.preventDefault();
                    e.stopPropagation();
                    const fileInput = document.createElement('input'); 
                    fileInput.type = 'file'; 
                    fileInput.accept = 'image/*'; 
                    fileInput.onchange = handleProfilePicChange; 
                    fileInput.click(); 
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: 'none',
                    borderRadius: '50%',
                    width: 'clamp(40px, 10vw, 50px)',
                    height: 'clamp(40px, 10vw, 50px)',
                    color: 'var(--rose)',
                    cursor: 'pointer',
                    fontSize: 'clamp(1rem, 3vw, 1.3rem)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    transition: 'var(--ease)',
                    padding: 0,
                    zIndex: 11,
                    pointerEvents: 'auto',
                    touchAction: 'manipulation'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Replace photo"
                >
                  ✏️
                </button>
              </div>
            )}
          </div>

          {/* Upload Input */}
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block',
              fontSize: 'clamp(0.85rem, 2vw, 0.95rem)',
              fontWeight: 500,
              marginBottom: 'clamp(0.5rem, 1.5vw, 0.75rem)',
              color: 'var(--text)'
            }}>
              Upload Your Best Photo
            </label>
            <div style={{
              position: 'relative',
              display: 'inline-block',
              width: '100%'
            }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePicChange}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer'
                }}
              />
              <div style={{
                padding: 'clamp(0.6rem, 1.5vw, 0.85rem) clamp(1rem, 2vw, 1.2rem)',
                background: 'var(--rose-l)',
                border: '1px dashed var(--rose)',
                borderRadius: 'var(--r)',
                textAlign: 'center',
                cursor: 'pointer',
                fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
                color: 'var(--rose)',
                fontWeight: 500,
                transition: 'var(--ease)'
              }}>
                Click to upload or drag & drop
              </div>
            </div>
            <p style={{
              fontSize: 'clamp(0.7rem, 1.3vw, 0.75rem)',
              color: 'var(--muted)',
              marginTop: 'clamp(0.4rem, 1vw, 0.6rem)'
            }}>
              JPG, PNG up to 5MB • Recommended: 400x500px
            </p>
            {profilePic && (
              <button
                type="button"
                onClick={() => setProfilePic(null)}
                style={{
                  marginTop: 'clamp(0.5rem, 1vw, 0.8rem)',
                  padding: 'clamp(0.3rem, 1vw, 0.4rem) clamp(0.8rem, 2vw, 1rem)',
                  background: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: 'var(--r)',
                  color: 'var(--rose)',
                  cursor: 'pointer',
                  fontSize: 'clamp(0.7rem, 1.3vw, 0.8rem)',
                  fontWeight: 500
                }}
              >
                ✕ Remove
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Gallery Photos Section */}
      <div className="card">
        <div style={{
          fontFamily: 'var(--serif)',
          fontSize: 'clamp(1rem, 2.5vw, 1.15rem)',
          fontWeight: 600,
          marginBottom: 'clamp(0.7rem, 2vw, 1.1rem)',
          paddingBottom: 'clamp(0.4rem, 1vw, 0.65rem)',
          borderBottom: '1px solid var(--cream-d)'
        }}>
          🖼️ Gallery (Up to 6 Photos)
        </div>
        <p style={{
          fontSize: 'clamp(0.75rem, 1.5vw, 0.85rem)',
          color: 'var(--muted)',
          marginBottom: 'clamp(0.8rem, 2vw, 1.2rem)'
        }}>
          Add more photos to showcase your personality and interests
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(100px, 25vw, 150px), 1fr))',
          gap: 'clamp(0.6rem, 2vw, 1rem)'
        }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} style={{ position: 'relative' }}>
              {/* Photo Preview */}
              <div
                onClick={() => { if (galleryPhotos[index]) { setSelectedPhoto({ type: 'gallery', url: galleryPhotos[index], index }); setPreviewModal(true); } }}
                onTouchStart={() => { if (galleryPhotos[index]) { setSelectedPhoto({ type: 'gallery', url: galleryPhotos[index], index }); setPreviewModal(true); } }}
                style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  borderRadius: 'var(--r)',
                  border: galleryPhotos[index] ? '2px solid var(--spice)' : '2px dashed var(--cream-d)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                  color: 'var(--cream-d)',
                  background: galleryPhotos[index] ? '#fff' : 'var(--cream-l)',
                  cursor: galleryPhotos[index] ? 'pointer' : 'default',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'var(--ease)',
                  boxShadow: galleryPhotos[index] ? '0 4px 12px rgba(100, 80, 60, 0.15)' : 'none',
                  pointerEvents: 'auto',
                  touchAction: 'manipulation'
                }}
                title={galleryPhotos[index] ? 'Tap to preview' : 'Click to upload'}
              >
                {galleryPhotos[index] ? (
                  <>
                    {/* Image */}
                    <img
                      src={galleryPhotos[index]}
                      alt={`Gallery ${index + 1}`}
                      onError={(e) => {
                        console.error(`Gallery ${index + 1} image failed to load:`, galleryPhotos[index]);
                        console.error('Error:', e);
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        padding: 'clamp(0.2rem, 1vw, 0.4rem)'
                      }}
                    />
                    
                    {/* Overlay with icons */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: visibleGalleryOverlay === index ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'clamp(0.3rem, 1.5vw, 0.6rem)',
                        transition: 'var(--ease)',
                        pointerEvents: 'auto'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0)';
                        setVisibleGalleryOverlay(null);
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        setVisibleGalleryOverlay(index);
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {/* Peek Icon */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedPhoto({ type: 'gallery', url: galleryPhotos[index], index });
                          setPreviewModal(true);
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: 'none',
                          borderRadius: '50%',
                          width: 'clamp(32px, 8vw, 44px)',
                          height: 'clamp(32px, 8vw, 44px)',
                          color: 'var(--spice)',
                          cursor: 'pointer',
                          fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          transition: 'var(--ease)',
                          padding: 0,
                          zIndex: 11,
                          pointerEvents: 'auto',
                          touchAction: 'manipulation'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title="Preview photo"
                      >
                        👁️
                      </button>

                      {/* Edit Icon */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const fileInput = document.createElement('input');
                          fileInput.type = 'file';
                          fileInput.accept = 'image/*';
                          fileInput.onchange = (evt) => handleGalleryPhotoChange(index, evt);
                          fileInput.click();
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: 'none',
                          borderRadius: '50%',
                          width: 'clamp(32px, 8vw, 44px)',
                          height: 'clamp(32px, 8vw, 44px)',
                          color: 'var(--spice)',
                          cursor: 'pointer',
                          fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          transition: 'var(--ease)',
                          padding: 0,
                          zIndex: 11,
                          pointerEvents: 'auto',
                          touchAction: 'manipulation'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title="Replace photo"
                      >
                        ✏️
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Hidden File Input for Initial Upload */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleGalleryPhotoChange(index, e)}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ cursor: 'pointer' }}>+</span>
                  </>
                )}
              </div>

              {/* Photo Number */}
              <div style={{
                fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)',
                fontWeight: 500,
                color: 'var(--mid)',
                textAlign: 'center',
                marginTop: 'clamp(0.3rem, 0.8vw, 0.5rem)'
              }}>
                Photo {index + 1}
              </div>

              {/* Remove Button */}
              {galleryPhotos[index] && (
                <button
                  type="button"
                  onClick={() => removeGalleryPhoto(index)}
                  style={{
                    position: 'absolute',
                    top: 'clamp(0.2rem, 0.5vw, 0.4rem)',
                    right: 'clamp(0.2rem, 0.5vw, 0.4rem)',
                    background: 'rgba(200, 70, 50, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: 'clamp(24px, 5vw, 32px)',
                    height: 'clamp(24px, 5vw, 32px)',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: 'clamp(0.8rem, 2vw, 1rem)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Preview Modal */}
      {previewModal && selectedPhoto && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Photo preview"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
            padding: 'clamp(1rem, 5vw, 2rem)'
          }}
          onClick={() => setPreviewModal(false)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: 'clamp(280px, 90vw, 600px)',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'clamp(0.8rem, 2vw, 1.2rem)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setPreviewModal(false)}
              aria-label="Close preview"
              style={{
                position: 'absolute',
                top: 'clamp(-2rem, -5vw, -1.5rem)',
                right: '0',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: 'clamp(32px, 8vw, 44px)',
                height: 'clamp(32px, 8vw, 44px)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 'clamp(1rem, 3vw, 1.5rem)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                transition: 'var(--ease)',
                zIndex: 10000
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
              title="Close (ESC)"
            >
              ✕
            </button>

            {/* Image Preview */}
            <img
              src={selectedPhoto.url}
              alt="Preview"
              onError={(e) => {
                console.error('Preview image failed to load:', selectedPhoto.url);
                console.error('Error:', e);
              }}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                borderRadius: 'var(--r)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
                objectFit: 'contain'
              }}
            />

            {/* Photo Info */}
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                padding: 'clamp(0.8rem, 2vw, 1.2rem)',
                borderRadius: 'var(--r)',
                textAlign: 'center',
                width: '100%',
                fontSize: 'clamp(0.8rem, 1.8vw, 0.95rem)',
                color: 'var(--text)',
                fontWeight: 500
              }}
            >
              {selectedPhoto.type === 'profile' ? 'Profile Picture' : `Gallery Photo ${selectedPhoto.index + 1}`}
            </div>

            {/* Keyboard hints for gallery */}
            {selectedPhoto.type === 'gallery' && (
              <div
                style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)',
                  textAlign: 'center',
                  marginTop: 'clamp(0.4rem, 1vw, 0.6rem)'
                }}
              >
                ← → arrows to navigate • ESC to close
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
